import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Target, Eye, Users, BarChart3, BookOpen, Award, ShieldCheck, Zap, Building2, Calendar } from "lucide-react";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import api from "../../lib/api";
import useSeo from "../../hooks/useSeo";

const TIMELINE = [
    { year: "2018", title: "Vizyon Doğdu", desc: "Sektörün dağınık bilgisini tek platformda toplama fikri ortaya çıktı." },
    { year: "2020", title: "Ekosistem Kuruldu", desc: "otel-urunleri, otel-malzemeleri ve otel-ekipmanlari kardeş markaları kuruldu." },
    { year: "2023", title: "Pilot Lansmanı", desc: "Sektör profesyonelleriyle yapılan pilot çalışmalarla beta sürüm test edildi." },
    { year: "2025", title: "Otel Pro Yayında", desc: "Türkiye'nin Otel Endüstrisi Platformu olarak resmi lansman." },
    { year: "2026", title: "AI ve Veri Analitiği", desc: "Yapay zeka destekli sektör tahminleri ve karar destek araçları." },
];

const VALUES = [
    { Icon: ShieldCheck, title: "Güvenilirlik", desc: "Bakanlık ve resmi kaynaklardan beslenen, doğrulanmış veriler." },
    { Icon: Zap, title: "Hız ve Pratiklik", desc: "Saniyeler içinde kararını destekleyecek araçlar ve raporlar." },
    { Icon: Award, title: "Premium Kalite", desc: "Yatırımcıdan genel müdüre, profesyonelin standardında içerik." },
    { Icon: Users, title: "Sektör Odaklı", desc: "Sadece otel endüstrisi için tasarlanmış, niş kaynak." },
];

const STATS = [
    { Icon: Building2, value: 14328, suffix: "", label: "Kayıtlı Tesis" },
    { Icon: Users, value: 5200, suffix: "+", label: "Sektör Profesyoneli" },
    { Icon: BarChart3, value: 81, suffix: "", label: "İl Verisi" },
    { Icon: BookOpen, value: 120, suffix: "+", label: "Sektör Yazısı" },
];

export default function About() {
    const { i18n } = useTranslation();
    const lang = (i18n.language || "tr").split("-")[0];
    const [page, setPage] = useState(null);
    useEffect(() => {
        let alive = true;
        api.get("/pages/hakkimizda")
            .then(({ data }) => {
                if (alive) setPage(data);
            })
            .catch(() => { });
        return () => {
            alive = false;
        };
    }, []);
    const localTitle = page?.title?.[lang] || page?.title?.tr;
    const localContent = page?.content?.[lang] || page?.content?.tr;
    useSeo({
        title: page?.seo?.meta_title || localTitle || "Hakkımızda",
        description: page?.seo?.meta_desc || "Otel Pro hakkında — Türkiye otel endüstrisinin başvuru platformu. Vizyon, misyon ve sektörel duruş.",
        path: "/hakkimizda",
    });

    return (
        <div className="bg-light-bg" data-testid="about-page">
            {/* HERO */}
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-5xl mx-auto px-6 py-16 lg:py-24 text-center">
                    <span className="text-xs uppercase tracking-[0.25em] text-teal font-semibold">Hakkımızda</span>
                    <h1 className="mt-3 text-4xl sm:text-6xl font-bold tracking-tight">{localTitle || "Sektörün Başvuru Noktası"}</h1>
                    <p className="mt-5 text-white/80 max-w-2xl mx-auto leading-relaxed">
                        Otel Pro, Türkiye otel endüstrisinin tek elden başvurabileceği veri merkezi, karar destek araçları ve sektörel yayın platformudur. Sektörü profesyoneller için sadeleştiriyoruz.
                    </p>
                </div>
            </section>

            {/* CMS CONTENT (admin TipTap) */}
            {localContent && (
                <section className="py-12 bg-white" data-testid="about-cms-content">
                    <div
                        className="max-w-3xl mx-auto px-6 prose prose-slate prose-headings:text-dark-navy prose-a:text-teal-700 prose-strong:text-dark-navy"
                        dangerouslySetInnerHTML={{ __html: localContent }}
                    />
                </section>
            )}

            {/* VISION + MISSION */}
            <section className="py-16">
                <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-6">
                    <div className="lift-card bg-white rounded-lg shadow-md hover:shadow-xl border border-slate-100 p-7" data-testid="about-vision">
                        <span className="inline-flex w-12 h-12 rounded-md bg-teal/10 text-teal-700 items-center justify-center">
                            <Eye className="w-5 h-5" />
                        </span>
                        <h2 className="mt-4 text-2xl font-bold text-dark-navy">Vizyonumuz</h2>
                        <p className="mt-3 text-deep-blue/80 leading-relaxed">
                            Türkiye otel endüstrisinin standardını oluşturan ve sektör paydaşlarının ilk başvurduğu güvenilir veri ve karar platformu olmak.
                        </p>
                    </div>
                    <div className="lift-card bg-white rounded-lg shadow-md hover:shadow-xl border border-slate-100 p-7" data-testid="about-mission">
                        <span className="inline-flex w-12 h-12 rounded-md bg-gold/15 text-gold-700 items-center justify-center">
                            <Target className="w-5 h-5" />
                        </span>
                        <h2 className="mt-4 text-2xl font-bold text-dark-navy">Misyonumuz</h2>
                        <p className="mt-3 text-deep-blue/80 leading-relaxed">
                            Otel sahipleri, yatırımcılar ve genel müdürlerin doğru karar vermesini sağlayacak veri, araç ve içeriği saniyeler içinde sunmak. Sektörün dağınık bilgisini tek platformda birleştirmek.
                        </p>
                    </div>
                </div>
            </section>

            {/* TIMELINE */}
            <section className="bg-white py-16">
                <div className="max-w-5xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <span className="text-xs uppercase tracking-[0.2em] text-teal-700 font-semibold">Yolculuğumuz</span>
                        <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-dark-navy">Bugüne Kadar</h2>
                    </div>
                    <div className="relative" data-testid="about-timeline">
                        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-teal via-gold to-deep-blue hidden md:block" />
                        <div className="space-y-8">
                            {TIMELINE.map((t, i) => (
                                <div key={t.year} className={`md:grid md:grid-cols-2 md:gap-12 items-center ${i % 2 === 0 ? "" : "md:[&>*:first-child]:order-2"}`}>
                                    <div className={`md:text-right ${i % 2 === 0 ? "md:pr-10" : "md:pl-10 md:text-left"}`}>
                                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 text-teal-700 font-data font-bold text-sm">
                                            <Calendar className="w-3.5 h-3.5" /> {t.year}
                                        </div>
                                        <h3 className="mt-3 text-xl font-bold text-dark-navy">{t.title}</h3>
                                        <p className="mt-1 text-sm text-deep-blue/80">{t.desc}</p>
                                    </div>
                                    <div className="hidden md:flex justify-center">
                                        <span className="w-4 h-4 rounded-full bg-teal ring-4 ring-teal/20" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="py-16">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-10">
                        <span className="text-xs uppercase tracking-[0.2em] text-teal-700 font-semibold">Rakamlarla Biz</span>
                        <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-dark-navy">Sektörün İçindeyiz</h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                        {STATS.map((s, i) => <AboutStat key={s.label} idx={i} {...s} />)}
                    </div>
                </div>
            </section>

            {/* VALUES */}
            <section className="bg-white py-16">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-10">
                        <span className="text-xs uppercase tracking-[0.2em] text-teal-700 font-semibold">Değerlerimiz</span>
                        <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-dark-navy">Neye İnanıyoruz</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {VALUES.map((v, i) => (
                            <div key={v.title} data-testid={`about-value-${i}`} className="lift-card bg-light-bg rounded-lg p-6 hover:bg-white border border-transparent hover:border-teal/30 hover:shadow-md">
                                <span className="inline-flex w-12 h-12 rounded-md bg-deep-blue/10 text-deep-blue items-center justify-center">
                                    <v.Icon className="w-5 h-5" />
                                </span>
                                <h3 className="mt-4 text-lg font-bold text-dark-navy">{v.title}</h3>
                                <p className="mt-2 text-sm text-deep-blue/80">{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

function AboutStat({ Icon, value, suffix, label, idx }) {
    const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
    return (
        <div ref={ref} data-testid={`about-stat-${idx}`} className="lift-card bg-white rounded-lg border border-slate-100 shadow-md p-6 text-center">
            <span className="inline-flex w-12 h-12 rounded-md bg-teal/10 text-teal-700 items-center justify-center">
                <Icon className="w-5 h-5" />
            </span>
            <div className="mt-3 font-data text-4xl font-bold text-dark-navy tabular-nums">
                {inView ? <CountUp end={value} duration={1.8} separator="." /> : "0"}
                {suffix}
            </div>
            <div className="mt-1 text-xs text-deep-blue/70 uppercase tracking-wider font-semibold">{label}</div>
        </div>
    );
}
